type FiberAddress = {street_name: string, zip_code: string, availability: boolean, fiberhood: string, service_area: string};

class FiberAvailabilityBlock {
	private static readonly fiberAddressCache = new Map<string, FiberAddress>();

	constructor(
		private readonly container: Element,
		private readonly form: HTMLFormElement,
		private readonly selected_address: HTMLSelectElement
	) {
		for (const fiberAddress of JSON.parse(form.getAttribute("data-initial-results") ?? "[]")) {
			FiberAvailabilityBlock.cacheAddress(`${fiberAddress.street_name}-${fiberAddress.zip_code}`, fiberAddress);
		}
		const useAjax = window.location.pathname === new URL(form.getAttribute("action")).pathname;
		if (useAjax) {
			form.setAttribute("action", form.getAttribute("data-ajax-url"));
			this.lookUpHouseAndZip();
		}
		form.addEventListener('submit', event => {
			form.classList.toggle("form-manually-submitted", true);
			if (event.submitter instanceof HTMLButtonElement && event.submitter.name === 'lookup') {
				event.preventDefault();
				if (useAjax) {
					this.lookUpHouseAndZip();
				} else {
					// We use a JS redirect in order to keep unnecessary parameters out of the url
					const formData = new FormData(form);
					const destination = new URL(form.getAttribute("action"));
					destination.searchParams.set("house_number", formData.get("house_number")?.toString() ?? "");
					destination.searchParams.set("zip_code", formData.get("zip_code")?.toString() ?? "");
					window.location.href = destination.toString();
				}
			}
		});
		selected_address.addEventListener('change', () => this.updateFiberAvailabilityDisplay());
	}

	private static cacheAddress(value: string, fiberAddress: Omit<FiberAddress, 'availability'>&{availability: boolean|'0'|'1'}) {
		FiberAvailabilityBlock.fiberAddressCache.set(value, {...fiberAddress,
			availability: fiberAddress.availability && fiberAddress.availability !== '0'})
	}

	private lookUpHouseAndZip() {
		// Form spinner
		this.form.classList.add('form--updating');

		fetch(this.form.getAttribute("action"), {method: 'post', body: new FormData(this.form)})
			.then(response => response.json())
			.then((response: {data: FiberAddress[]}) => {
				const options = [this.selected_address.options.item(0)];
				for (const fiberAddress of response.data) {
					const value = `${fiberAddress.street_name}-${fiberAddress.zip_code}`;
					const option = document.createElement("option");
					option.value = value;
					option.innerText = fiberAddress.street_name;
					options.push(option);
					FiberAvailabilityBlock.cacheAddress(value, fiberAddress);
				}
				this.selected_address.value = "";
				this.selected_address.replaceChildren(...options);
				this.updateFiberAvailabilityDisplay(options.length < 2 && this.form.classList.contains("form-manually-submitted") ? "Unable to find any matching addresses." : undefined);
			})
			.catch(error => this.updateFiberAvailabilityDisplay(error))
			.finally(() => {
				this.form.classList.remove('form--updating');
			});
	}

	private updateFiberAvailabilityDisplay(error?: string) {
		this.container.classList.remove("state-error");
		this.container.classList.remove("state-available");
		this.container.classList.remove("state-unavailable");

		const fiberError = this.container.querySelector(".fiber-error");
		const fiberAvailable = this.container.querySelector(".fiber-available");
		const fiberUnavailable = this.container.querySelector(".fiber-unavailable");
		if (!(fiberError instanceof HTMLElement) || !(fiberAvailable instanceof HTMLElement) || !(fiberUnavailable instanceof HTMLElement)) {
			return;
		}
		fiberUnavailable.querySelectorAll<HTMLAnchorElement>("a.express-interest")
			.forEach(link => link.href = link.getAttribute("data-base-href"));

		if (error) {
			this.container.classList.toggle("state-error", true);
			const errorMessageHolder = document.createElement("p");
			errorMessageHolder.classList.add("error");
			errorMessageHolder.append(`An error occurred: ${error}`);
			fiberError.replaceChildren(errorMessageHolder);
			return;
		}

		const fiberAddress = FiberAvailabilityBlock.fiberAddressCache.get(this.selected_address.value);
		if (!fiberAddress) {
			fiberUnavailable.querySelectorAll<HTMLAnchorElement>("a.express-interest")
				.forEach(link => link.href = link.getAttribute("data-base-href"));
			return;
		}

		if (fiberAddress.availability) {
			this.container.classList.toggle("state-available", true);
		} else {
			this.container.classList.toggle("state-unavailable", true);
			//TODO: If client says that not all cities are "Webster City", this will need expanding
			const query = new URLSearchParams([["chkpt-city", "Webster City"], ["chkpt-fiberhood", fiberAddress.fiberhood], ["chkpt-state", "IA"],
				["chkpt-street", fiberAddress.street_name], ["chkpt-zip", fiberAddress.zip_code]]).toString();
			fiberUnavailable.querySelectorAll<HTMLAnchorElement>("a.express-interest").forEach(link => {
				return link.href = `${link.getAttribute("data-base-href")}?${query}`;
			});
		}
	}
}
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll(".wp-block-plaudit-fiber-availability").forEach(container => {
		if (!container.classList.contains("available-fiber-set-up")) {
			container.classList.toggle("available-fiber-set-up", true);
			const form = container.querySelector("form");
			if (!form?.action) {
				return;
			}

			const selected_address = form.elements.namedItem("selected_address");
			if (!(selected_address instanceof HTMLSelectElement)) {
				return;
			}
			new FiberAvailabilityBlock(container, form, selected_address);
		}
	});

	// Update specific links on page load
	updateFiberAvailableLinks();
});

function updateFiberAvailableLinks() {
    const selectElement = document.querySelector('.selected-address') as HTMLSelectElement;
    const fiberAvailableLinks = document.querySelectorAll<HTMLAnchorElement>('.fiber-available-link');
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');

    if(selectElement) {
        selectElement.addEventListener('change', function() {
            const selectedAddress = selectElement.value;
            const formattedAddress = selectedAddress.replace(/\s/g, '+');
            const [street, zip] = selectedAddress.split('-');

            fiberAvailableLinks.forEach(link => {
				// get base href without query string
				const baseHref = (link.getAttribute('data-base-href') || link.getAttribute('href') || '').split('?')[0];
                let newHref = `${baseHref}?address=${encodeURIComponent(formattedAddress)}`;

                if (link.classList.contains('sign-up')) {
                    newHref = `${baseHref}?street=${encodeURIComponent(street)}&zip=${encodeURIComponent(zip)}`;
                    if (planParam) {
                        newHref += `&plan=${encodeURIComponent(planParam)}`;
                    }
                }

                link.setAttribute('href', newHref);
            });
        });
    }
}
